import axios from "axios"
import accessToken from "./jwt-token-access/accessToken"
import { server } from "../store/globalSettings"

const DEFAULT_SUCCESS_CODES = ["success", "validate_success"]
const DEFAULT_ERROR_MESSAGE = "Something went wrong. Please try again."

const getStoredToken = () => {
  const storedToken =
    localStorage.getItem("token") || sessionStorage.getItem("token")

  if (!storedToken) return null

  try {
    return JSON.parse(storedToken)
  } catch (error) {
    return storedToken
  }
}

const createApiError = ({ status, code, message, errors, data, cause }) => {
  const apiError = new Error(message || DEFAULT_ERROR_MESSAGE)

  apiError.name = "ApiError"
  apiError.status = status || 0
  apiError.code = code || "request_failed"
  apiError.errors = errors || {}
  apiError.data = data || null

  if (cause) apiError.cause = cause

  return apiError
}

/**
 * Shared request handler for the application's API endpoints.
 *
 * Successful requests return the parsed API response. Failed requests throw
 * one consistent error containing status, code, message, errors and data.
 */
export async function apiRequest(
  endpoint,
  {
    method = "GET",
    data,
    auth = true,
    headers = {},
    successCodes = DEFAULT_SUCCESS_CODES,
    ...fetchOptions
  } = {}
) {
  const normalizedMethod = method.toUpperCase()
  const canHaveBody = !["GET", "HEAD"].includes(normalizedMethod)
  const isFormData =
    typeof FormData !== "undefined" && data instanceof FormData
  const token = auth ? getStoredToken() : null
  const authorization = token
    ? String(token).startsWith("Bearer ")
      ? String(token)
      : `Bearer ${token}`
    : null
  const url = /^https?:\/\//i.test(endpoint) ? endpoint : `${server}${endpoint}`

  let response

  try {
    response = await fetch(url, {
      ...fetchOptions,
      method: normalizedMethod,
      headers: {
        ...(!isFormData && canHaveBody
          ? { "Content-Type": "application/json" }
          : {}),
        ...(authorization ? { Authorization: authorization } : {}),
        ...headers,
      },
      ...(canHaveBody && data !== undefined
        ? { body: isFormData ? data : JSON.stringify(data) }
        : {}),
    })
  } catch (error) {
    throw createApiError({
      status: 0,
      code: "network_error",
      message: "Unable to connect to the server.",
      errors: {},
      data: null,
      cause: error,
    })
  }

  const responseText = await response.text()
  let responseData = null

  if (responseText) {
    try {
      responseData = JSON.parse(responseText)
    } catch (error) {
      responseData = response.ok ? responseText : null
    }
  }

  const hasApplicationError =
    responseData &&
    typeof responseData === "object" &&
    responseData.code &&
    !successCodes.includes(responseData.code)

  if (!response.ok || hasApplicationError) {
    throw createApiError({
      status: response.status,
      code: responseData?.code || `http_${response.status}`,
      message:
        responseData?.message ||
        responseData?.error_message ||
        responseData?.data?.error_message ||
        DEFAULT_ERROR_MESSAGE,
      errors: responseData?.errors || {},
      data: responseData,
    })
  }

  return responseData
}

//pass new generated access token here
const token = accessToken

//apply base url for axios
const API_URL = ""

const axiosApi = axios.create({
  baseURL: API_URL,
})

axiosApi.defaults.headers.common["Authorization"] = token

axiosApi.interceptors.response.use(
  response => response,
  error => Promise.reject(error)
)

export async function get(url, config = {}) {
  return await axiosApi.get(url, { ...config }).then(response => response.data)
}

export async function post(url, data, config = {}) {
  return axiosApi
    .post(url, { ...data }, { ...config })
    .then(response => response.data)
}

export async function put(url, data, config = {}) {
  return axiosApi
    .put(url, { ...data }, { ...config })
    .then(response => response.data)
}

export async function del(url, config = {}) {
  return await axiosApi
    .delete(url, { ...config })
    .then(response => response.data)
}
